import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Select, message } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import GoogleMapPicker from '../../components/GoogleMapPicker';
import {
  ResourceBasicIcon,
  ResourceImageIcon,
  LocationPinIcon,
  PersonIcon,
  CaretIcon,
} from '../../assets/icons';
import {
  useGetResourceQuery,
  useGetResourceFiltersQuery,
  useCreateResourceMutation,
  useUpdateResourceMutation,
  type ResourcePayload,
} from '../../features/resources/resourcesApi';

/* ------------------------------------------------------------------ *
 * Add Resource (Resource Directory → Add). Figma 1657-674.
 * Three sections — Basic & Categorization, Location & Mapping, Key Person —
 * plus a sticky Discard / Save as Draft / Publish toolbar. Demo (no API yet).
 * ------------------------------------------------------------------ */

export const CATEGORIES = ['Education', 'Health', 'Religious', 'Infrastructure', 'Public Safety', 'Community'];
export const SUB_CATEGORIES = [
  'Primary School',
  'Secondary School',
  'Senior Secondary School',
  'High School',
  'Hospital',
  'Primary Health Centre',
  'Temple',
  'Community Hall',
];
export const RESOURCE_TYPES = ['School', 'College', 'Hospital', 'Clinic', 'Place of Worship', 'Office'];
const LOCATION_LEVELS = ['State', 'District', 'Assembly', 'Block', 'Gram Panchayat', 'Village', 'Booth'];

/** Design-matched field styling (slate-200 border, rounded, focus ring); red when invalid. */
const inputCls = (invalid?: string) =>
  [
    'w-full rounded-lg border bg-white px-[15px] py-3 text-sm text-ink placeholder:text-[#94a3b8] transition-colors focus:outline-none focus:ring-2',
    invalid
      ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
      : 'border-[#e2e8f0] focus:border-brand focus:ring-brand/15',
  ].join(' ');

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-xs font-semibold leading-4 text-[#334155]">
      {children}
      {required && <span className="font-semibold text-[#ef4444]"> *</span>}
    </label>
  );
}

/** Field footer: an error (when touched + invalid) or the grey helper hint. */
function Note({ error, children }: { error?: string; children?: React.ReactNode }) {
  if (error) return <p className="text-[11px] font-medium leading-[16.5px] text-[#ba1a1a]">{error}</p>;
  return children ? <p className="text-[11px] leading-[16.5px] text-[#94a3b8]">{children}</p> : null;
}

interface ResourceForm {
  category: string;
  subCategory: string;
  resourceType: string;
  name: string;
  locationLevel: string;
  address: string;
  pincode: string;
  officer: string;
  designation: string;
  mobile: string;
  altPhone: string;
  email: string;
  latitude: string;
  longitude: string;
}

const EMPTY_FORM: ResourceForm = {
  category: '',
  subCategory: '',
  resourceType: '',
  name: '',
  locationLevel: '',
  address: '',
  pincode: '',
  officer: '',
  designation: '',
  mobile: '',
  altPhone: '',
  email: '',
  latitude: '',
  longitude: '',
};

/** Full validation enforced on Publish (Save as Draft only needs a name). */
const validationSchema = Yup.object({
  category: Yup.string().required('Select a category'),
  subCategory: Yup.string().required('Select a sub-category'),
  resourceType: Yup.string().required('Select a resource type'),
  name: Yup.string().trim().required('Institution name is required'),
  locationLevel: Yup.string().required('Select a location level'),
  address: Yup.string().trim().required('Street address is required'),
  pincode: Yup.string()
    .trim()
    .required('Pincode is required')
    .matches(/^\d{6}$/, 'Enter a valid 6-digit pincode'),
  officer: Yup.string().trim().required('Officer name is required'),
  designation: Yup.string().trim().required('Designation / role is required'),
  mobile: Yup.string()
    .trim()
    .required('Mobile number is required')
    .matches(/^\d{10}$/, 'Enter a valid 10-digit mobile number'),
  altPhone: Yup.string(),
  email: Yup.string().trim().email('Enter a valid email address'),
});

/** One form section card — icon badge + title/subtitle header, right-side pill, body. */
function SectionCard({
  icon,
  title,
  subtitle,
  badge,
  badgeTone = 'blue',
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  badge: string;
  badgeTone?: 'blue' | 'slate';
  children: React.ReactNode;
}) {
  const badgeCls =
    badgeTone === 'blue'
      ? 'bg-[#eff6ff] border-[#dbeafe] text-[#1d4ed8]'
      : 'bg-[#f1f5f9] border-[#e2e8f0] text-[#475569]';
  return (
    <section className="flex flex-col gap-6 rounded-xl border border-[rgba(226,232,240,0.9)] bg-white p-[25px] shadow-card">
      <header className="flex items-start justify-between gap-4 border-b border-[#f1f5f9] pb-5">
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#eff6ff] text-brand">
            {icon}
          </span>
          <div className="flex flex-col gap-0.5">
            <h2 className="text-base font-bold leading-6 text-[#0f172a]">{title}</h2>
            <p className="text-xs leading-4 text-[#64748b]">{subtitle}</p>
          </div>
        </div>
        <span className={['shrink-0 rounded-md border px-[11px] py-[3px] text-[11px] font-semibold leading-[16.5px]', badgeCls].join(' ')}>
          {badge}
        </span>
      </header>
      {children}
    </section>
  );
}

export default function AddResourcePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  // Reusable from the client flow: return to ?returnTo= on save/cancel, scope to ?org=.
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/admin/resource-directory';
  const orgParam = searchParams.get('org');
  const caret = <CaretIcon className="size-3.5 text-[#64748b]" />;

  const { data: editResource } = useGetResourceQuery(id!, { skip: !isEdit });
  const { data: filters } = useGetResourceFiltersQuery();
  const [createResource, { isLoading: creating }] = useCreateResourceMutation();
  const [updateResource, { isLoading: updating }] = useUpdateResourceMutation();
  const saving = creating || updating;

  const save = async (status: 'Draft' | 'Published', values: ResourceForm) => {
    const payload: ResourcePayload = {
      name: values.name.trim(),
      category: values.category || undefined,
      sub_category: values.subCategory || undefined,
      resource_type: values.resourceType || undefined,
      location_level: values.locationLevel || undefined,
      address: values.address.trim() || undefined,
      pincode: values.pincode.trim() || undefined,
      incharge_name: values.officer.trim() || undefined,
      incharge_designation: values.designation.trim() || undefined,
      mobile: values.mobile.trim() || undefined,
      alternate_phone: values.altPhone.trim() || undefined,
      email: values.email.trim() || undefined,
      latitude: values.latitude || undefined,
      longitude: values.longitude || undefined,
      status,
      ...(orgParam ? { organization: Number(orgParam) } : {}),
    };
    try {
      if (isEdit && id) {
        await updateResource({ id, patch: payload }).unwrap();
        message.success(status === 'Draft' ? 'Saved as draft.' : 'Resource updated.');
      } else {
        await createResource(payload).unwrap();
        message.success(status === 'Draft' ? 'Saved as draft.' : 'Resource published.');
      }
      navigate(returnTo);
    } catch {
      message.error('Could not save the resource. Please try again.');
    }
  };

  const formik = useFormik<ResourceForm>({
    initialValues: EMPTY_FORM,
    validationSchema,
    onSubmit: (values) => save('Published', values),
  });
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formik;
  const errorOf = (f: keyof ResourceForm) =>
    touched[f] && errors[f] ? (errors[f] as string) : undefined;

  // Edit mode — prefill once the resource loads.
  useEffect(() => {
    if (!editResource) return;
    formik.setValues({
      category: editResource.category ?? '',
      subCategory: editResource.sub_category ?? '',
      resourceType: editResource.resource_type ?? '',
      name: editResource.name ?? '',
      locationLevel: editResource.location_level ?? '',
      address: editResource.address ?? '',
      pincode: editResource.pincode ?? '',
      officer: editResource.incharge_name ?? '',
      designation: editResource.incharge_designation ?? '',
      mobile: editResource.mobile ?? '',
      altPhone: editResource.alternate_phone ?? '',
      email: editResource.email ?? '',
      latitude: editResource.latitude ?? '',
      longitude: editResource.longitude ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editResource]);

  // Dropdown options come from the backend, falling back to the static seed lists.
  const categoryOpts = filters?.categories?.length ? filters.categories : CATEGORIES;
  const subCategoryOpts = filters?.sub_categories?.length ? filters.sub_categories : SUB_CATEGORIES;
  const resourceTypeOpts = filters?.resource_types?.length ? filters.resource_types : RESOURCE_TYPES;

  // A Select bound to a Formik field (value, change + touched, error status).
  const selectProps = (field: keyof ResourceForm) => ({
    value: values[field] || undefined,
    onChange: (v: string) => setFieldValue(field, v ?? ''),
    onBlur: () => setFieldTouched(field, true),
    status: errorOf(field) ? ('error' as const) : undefined,
  });

  // Save as Draft — only the name is required; everything else can be filled later.
  const saveDraft = () => {
    if (!values.name.trim()) {
      setFieldTouched('name', true, false);
      formik.setFieldError('name', 'Institution name is required');
      message.error('Institution name is required.');
      return;
    }
    save('Draft', values);
  };

  return (
    <div className="flex flex-col gap-6 pb-24">
      {/* Breadcrumb + header */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            ...(returnTo.startsWith('/client')
              ? [{ label: 'Resources', to: returnTo }]
              : [
                  { label: 'Dashboard', to: '/admin' },
                  { label: 'Resource Directory', to: '/admin/resource-directory' },
                ]),
            { label: isEdit ? 'Edit Resource' : 'Add Resource' },
          ]}
        />
        <h1 className="pt-1 text-2xl font-semibold leading-8 tracking-[-0.6px] text-ink">
          {isEdit ? 'Edit Resource' : 'Add Resources'}
        </h1>
        <p className="text-sm leading-5 text-ink-secondary">
          Enter details to register a new public facility, institution, or community resource in the
          constituency directory.
        </p>
      </div>

      {/* Card 1 — Basic & Categorization */}
      <SectionCard
        icon={<ResourceBasicIcon className="size-5" />}
        title="1. Basic & Categorization Details"
        subtitle="Core institutional nomenclature, sector classification, and operational status."
        badge="Primary Step"
      >
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="form-field-select flex flex-col gap-1">
              <FieldLabel required>Category</FieldLabel>
              <Select
                className="w-full"
                size="large"
                placeholder="Select category"
                options={categoryOpts.map((o) => ({ value: o, label: o }))}
                suffixIcon={caret}
                {...selectProps('category')}
              />
              <Note error={errorOf('category')}>Classifies governance domain for reporting.</Note>
            </div>
            <div className="form-field-select flex flex-col gap-1">
              <FieldLabel required>Sub-Category</FieldLabel>
              <Select
                className="w-full"
                size="large"
                placeholder="Select specific type"
                options={subCategoryOpts.map((o) => ({ value: o, label: o }))}
                suffixIcon={caret}
                {...selectProps('subCategory')}
              />
              <Note error={errorOf('subCategory')}>Granular unit definition for caseworkers.</Note>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="form-field-select flex flex-col gap-1.5">
              <FieldLabel required>Resource Type</FieldLabel>
              <Select
                className="w-full"
                size="large"
                placeholder="Select Type"
                options={resourceTypeOpts.map((o) => ({ value: o, label: o }))}
                suffixIcon={caret}
                {...selectProps('resourceType')}
              />
              <Note error={errorOf('resourceType')} />
            </div>
            <div className="flex flex-col gap-1.5">
              <FieldLabel required>Institution Name</FieldLabel>
              <input
                name="name"
                className={inputCls(errorOf('name'))}
                placeholder="e.g. Government Higher Secondary School, Station Road"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Note error={errorOf('name')} />
            </div>
          </div>

          {/* Resource image upload */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel>Resource Image</FieldLabel>
            <button
              type="button"
              onClick={() => message.info('Image upload coming soon.')}
              className="flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-[#e2e8f0] bg-[#f8fafc]/50 p-[22px] text-center transition-colors hover:border-brand/50 hover:bg-brand-shade/30"
            >
              <ResourceImageIcon className="size-8 text-[#3b82f6]/80" />
              <span className="pt-1 text-xs">
                <span className="font-semibold text-[#2563eb]">Click to upload</span>
                <span className="font-medium text-[#334155]"> or drag and drop facility photo</span>
              </span>
              <span className="text-[11px] leading-[16.5px] text-[#94a3b8]">
                PNG, JPG, or WEBP up to 5MB (Geo-tagged pictures preferred)
              </span>
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Card 2 — Location & Mapping */}
      <SectionCard
        icon={<LocationPinIcon className="size-5" />}
        title="2. Location"
        subtitle="Electoral zoning, territorial markers, and spatial coordinates."
        badge="GIS Boundary"
        badgeTone="slate"
      >
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
            <div className="form-field-select flex flex-col gap-1">
              <FieldLabel required>Location Level</FieldLabel>
              <Select
                className="w-full"
                size="large"
                placeholder="Level"
                options={LOCATION_LEVELS.map((o) => ({ value: o, label: o }))}
                suffixIcon={caret}
                {...selectProps('locationLevel')}
              />
              <Note error={errorOf('locationLevel')}>Constituency hierarchy echelon.</Note>
            </div>
            <div className="flex flex-col gap-1 md:col-span-2">
              <FieldLabel required>Street Address &amp; Notable Landmark</FieldLabel>
              <input
                name="address"
                className={inputCls(errorOf('address'))}
                placeholder="e.g. Opposite Central Post Office, Station Road"
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Note error={errorOf('address')}>
                Include cross street, landmark, and physical signage reference.
              </Note>
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel required>Postal Index (Pincode)</FieldLabel>
              <input
                name="pincode"
                inputMode="numeric"
                maxLength={6}
                className={inputCls(errorOf('pincode'))}
                placeholder="301001"
                value={values.pincode}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Note error={errorOf('pincode')}>6-digit standard postal code.</Note>
            </div>
          </div>

          {/* Coordinates + boundary map */}
          <div className="flex flex-col gap-2 pt-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <FieldLabel>Geographical Coordinates &amp; Boundary Map</FieldLabel>
              {values.latitude && values.longitude ? (
                <span className="inline-flex items-center gap-1.5 rounded border border-[#a7f3d0] bg-[#ecfdf5] px-2.5 py-[3px]">
                  <span className="size-1.5 rounded-full bg-[#10b981]" />
                  <span className="font-mono text-[11px] text-[#047857]">
                    Lat: {values.latitude}, Long: {values.longitude}
                  </span>
                </span>
              ) : (
                <span className="text-[11px] text-[#94a3b8]">
                  Click the map or drag the pin to set the location.
                </span>
              )}
            </div>
            <GoogleMapPicker
              lat={values.latitude ? Number(values.latitude) : undefined}
              lng={values.longitude ? Number(values.longitude) : undefined}
              onChange={(la, lo) => {
                setFieldValue('latitude', la.toFixed(6));
                setFieldValue('longitude', lo.toFixed(6));
              }}
              onAddress={(addr, pin) => {
                if (addr) setFieldValue('address', addr);
                if (pin) setFieldValue('pincode', pin);
              }}
            />
          </div>
        </div>
      </SectionCard>

      {/* Card 3 — Key Person & In-Charge */}
      <SectionCard
        icon={<PersonIcon className="size-5" />}
        title="3. Key Person & In-Charge Details"
        subtitle="Designated administrative head, emergency point-of-contact, and official outreach."
        badge="Accountability"
        badgeTone="slate"
      >
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <FieldLabel required>In-Charge / Officer Full Name</FieldLabel>
              <div className="relative">
                <PersonIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#94a3b8]" />
                <input
                  name="officer"
                  className={[inputCls(errorOf('officer')), 'pl-10'].join(' ')}
                  placeholder="e.g. Dr. Rajesh Sharma"
                  value={values.officer}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <Note error={errorOf('officer')}>Full name with prefix (Dr./Er./Shri/Smt.).</Note>
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel required>Designation / Role Title</FieldLabel>
              <input
                name="designation"
                className={inputCls(errorOf('designation'))}
                placeholder="e.g. Principal / Chief Medical Officer / SHO"
                value={values.designation}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Note error={errorOf('designation')}>Official executive post held at the resource.</Note>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <FieldLabel required>Primary Mobile Number</FieldLabel>
              <div
                className={[
                  'flex h-11 items-stretch overflow-hidden rounded-lg border transition-colors focus-within:ring-2',
                  errorOf('mobile')
                    ? 'border-red-400 focus-within:border-red-500 focus-within:ring-red-100'
                    : 'border-[#e2e8f0] focus-within:border-brand focus-within:ring-brand/15',
                ].join(' ')}
              >
                <span className="flex items-center border-r border-[#e2e8f0] bg-[#f8fafc] px-3.5 text-xs font-semibold text-[#475569]">
                  +91
                </span>
                <input
                  name="mobile"
                  inputMode="numeric"
                  maxLength={10}
                  className="min-w-0 flex-1 px-3.5 text-sm text-ink placeholder:text-[#94a3b8] focus:outline-none"
                  placeholder="98765 43210"
                  value={values.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <Note error={errorOf('mobile')}>Direct mobile used for emergency notifications.</Note>
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel>Alternate Phone / Landline</FieldLabel>
              <input
                name="altPhone"
                className={inputCls(errorOf('altPhone'))}
                placeholder="e.g. 0144-234567"
                value={values.altPhone}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Note error={errorOf('altPhone')}>Office front desk or reception desk line.</Note>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <FieldLabel>Official Email Address</FieldLabel>
            <input
              name="email"
              type="email"
              className={inputCls(errorOf('email'))}
              placeholder="e.g. officer.alwar@rajasthan.gov.in"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <Note error={errorOf('email')}>
              Preferred official .gov.in or authorized institutional mailbox.
            </Note>
          </div>
        </div>
      </SectionCard>

      {/* Sticky action toolbar */}
      <div className="sticky bottom-0 z-10 -mx-4 flex items-center justify-end gap-2.5 border-t border-line-soft bg-white px-4 py-4 sm:-mx-6 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(returnTo)}
          className="h-10 rounded-lg bg-brand-shade px-4 text-[13px] font-medium text-[#0b1c30] transition-colors hover:bg-[#e0ebff]"
        >
          Discard
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={saveDraft}
          className="h-10 rounded-lg border border-brand px-4 text-[13px] font-medium text-brand transition-colors hover:bg-brand-shade disabled:opacity-60"
        >
          Save as Draft
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => formik.submitForm()}
          className="h-10 rounded-lg bg-brand px-5 text-[13px] font-medium text-white shadow-card transition-colors hover:bg-brand-dark disabled:opacity-60"
        >
          {saving ? 'Saving…' : isEdit ? 'Update Resource' : 'Publish Resource'}
        </button>
      </div>
    </div>
  );
}
